import { useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import CustomModal from './CustomModal';
import CameraScanInput from './CameraScanInput';
import showToast from './ToastComponent';
import useVerifyProductBarcode from '../hooks/useVerifyProductBarcode';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import {
  OrderScanCheckModalProps,
  OrderLineItem,
} from '../interface/interface';

export default function OrderScanCheckModal({
  isOpen,
  onClose,
  orderId,
  lineItems,
  onProcess,
  showProceedButton = true,
}: Readonly<OrderScanCheckModalProps>) {
  const { user } = useAuth();
  const [scannedItems, setScannedItems] = useState<Record<string, number>>({});
  const [scanError, setScanError] = useState<string>('');
  const { mutateAsync: verifyProductBarcode, isPending: isVerifying } =
    useVerifyProductBarcode();
  const isVerifyingRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setScannedItems({});
      setScanError('');
      isVerifyingRef.current = false;
    }
  }, [isOpen]);

  const isItemComplete = (item: OrderLineItem, scannedCount: number) =>
    scannedCount >= item.quantity || item.isVerified;

  const handleBarcodeScanned = async (barcode: string) => {
    if (isVerifyingRef.current) return;
    isVerifyingRef.current = true;
    setScanError('');

    const remainingItems = lineItems.filter(
      (item) => !isItemComplete(item, scannedItems[item.id] ?? 0),
    );

    try {
      const result = await verifyProductBarcode({
        accessToken: user?.access_token ?? '',
        orderId,
        barcode,
      });

      const verifiedResult =
        result && typeof result !== 'number' && result.verified
          ? result
          : undefined;
      const matchedItem = verifiedResult
        ? remainingItems.find(
            (item) =>
              String(item.productId) === String(verifiedResult.productId),
          )
        : undefined;

      if (matchedItem) {
        const matched = matchedItem;
        setScannedItems((prev) => ({
          ...prev,
          [matched.id]: Math.min((prev[matched.id] ?? 0) + 1, matched.quantity),
        }));
        showToast({ message: `${matched.name} scanned`, type: 'success' });
      } else {
        const message = 'Scanned barcode does not match any products';
        setScanError(message);
        showToast({ message, type: 'error' });
      }
    } catch (error) {
      console.error(error);
      const message = 'Unable to verify barcode. Please try again.';
      setScanError(message);
      showToast({ message, type: 'error' });
    } finally {
      isVerifyingRef.current = false;
    }
  };

  const allItemsScanned =
    lineItems.length > 0 &&
    lineItems.every((item) => isItemComplete(item, scannedItems[item.id] ?? 0));

  const handleProcess = () => {
    onProcess();
    onClose();
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      header="Scan Check"
      footer=""
      modalSize="w-1/4"
      disableBackdropClose={true}
    >
      <div className="flex flex-col">
        <CameraScanInput
          onScan={handleBarcodeScanned}
          disabled={isVerifying || allItemsScanned}
          error={scanError}
        />
        {isVerifying && (
          <p className="text-xs text-gray-500 text-center">
            Verifying barcode…
          </p>
        )}

        <ul className="divide-y divide-gray-100 bg-gray-100 shadow-lg rounded-md px-4">
          {lineItems.map((item) => {
            const scannedCount = scannedItems[item.id] ?? 0;
            const isPreviouslyVerified = item.isVerified && scannedCount === 0;
            const isComplete = isItemComplete(item, scannedCount);

            let statusText: string;
            if (isPreviouslyVerified) {
              statusText = 'Verified';
            } else {
              statusText = `${scannedCount}/${item.quantity} scanned`;
            }

            return (
              <li
                key={item.id}
                className="flex justify-between items-center py-2"
              >
                <div>
                  <p className="text-sm font-medium">{item.name}</p>
                  <p className="text-xs text-gray-500">{statusText}</p>
                </div>

                <div className="relative shrink-0 rounded-full p-3 bg-gray-100">
                  {isComplete && (
                    <CheckCircleIcon className="w-5 h-5 text-green-600 absolute bottom-1 -right-1 bg-white rounded-full" />
                  )}
                </div>
              </li>
            );
          })}
        </ul>

        {allItemsScanned && (
          <div className="flex flex-col items-center gap-2 text-green-600 mt-2">
            {showProceedButton ? (
              <button
                type="button"
                onClick={handleProcess}
                className="mt-2 bg-primary-blue-500 text-white px-6 py-2 rounded-full text-sm font-semibold"
              >
                Proceed
              </button>
            ) : (
              <p className="flex items-center gap-2 text-sm font-semibold">
                <CheckCircleIcon className="w-5 h-5" />
                All items verified
              </p>
            )}
          </div>
        )}
      </div>
    </CustomModal>
  );
}
